import React, { useState } from 'react';
import {
  faEye,
  faEyeSlash,
  faEnvelope,
  faLock,
  faSignInAlt
} from '@fortawesome/free-solid-svg-icons';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth } from '../hooks/useAuth';
import FormInput from '../components/FormInput';
import Button from '../components/Button';
import Alert from '../components/Alert';
import { loginSchema, LoginFormData } from '../schemas';
import { isSignupEnabled } from '../config/config';

const LoginPage: React.FC = () => {
  const { login, error, user, loading, currentOrganization } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const isSignupSuccess = searchParams.get('signup') === 'success';

  const {
    control,
    handleSubmit,
    watch,
    formState: { isValid }
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    mode: 'onChange',
    defaultValues: {
      email: '',
      password: ''
    }
  });

  const watchedFields = watch();

  const onSubmit = async (data: LoginFormData): Promise<void> => {
    setIsLoading(true);

    try {
      const { error } = await login(data.email, data.password);
      if (!error) {
        navigate('/');
      }
    } catch (err) {
      // Error handled by useAuth
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = (): void => {
    setShowPassword(!showPassword);
  };

  if (user && !loading) {
    return <Navigate to="/" replace />;
  }

  return (
    <>
      {/* Header */}
      <div className="text-center mb-6">
        <div className="flex justify-center mb-4">
          <div className="relative">
            <div className="w-16 h-16 bg-white rounded-xl shadow-lg flex items-center justify-center border border-gray-100">
              <img
                className="w-10 h-10 object-contain"
                src={currentOrganization?.logo_url || '/logoipsum-custom-logogram.svg'}
                alt={`${currentOrganization?.name || 'OptoSoft'} Logo`}
              />
            </div>
          </div>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-1">
          {currentOrganization?.name || 'OptoSoft'}
        </h1>
        <p className="text-gray-600 text-sm">
          {currentOrganization?.slogan || 'Sistema de Gestão Clínica'}
        </p>
      </div>

      {/* Login Form */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
        <div className="mb-5">
          <h2 className="text-xl font-semibold text-gray-900 mb-1">Acesso ao Sistema</h2>
          <p className="text-gray-600 text-sm">Digite suas credenciais para acessar</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          {/* Email Field */}
          <FormInput
            name="email"
            control={control}
            id="email"
            type="email"
            autoComplete="email"
            required
            label="Email"
            leftIcon={faEnvelope}
            placeholder="seu@email.com"
          />

          {/* Password Field */}
          <FormInput
            name="password"
            control={control}
            id="password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            required
            label="Senha"
            leftIcon={faLock}
            rightIcon={showPassword ? faEyeSlash : faEye}
            onRightIconClick={togglePasswordVisibility}
            placeholder="••••••••"
          />

          {/* Forgot Password Link */}
          <div className="text-right">
            <a
              href="/forgot-password"
              className="text-sm text-blue-600 hover:text-blue-700 transition-colors"
            >
              Esqueceu a senha?
            </a>
          </div>

          {/* Success Message */}
          {isSignupSuccess && (
            <Alert
              variant="success"
              title="Conta Criada com Sucesso!"
              message="Verifique seu email para ativar sua conta e faça login aqui."
            />
          )}

          {/* Error Message */}
          {error && (
            <Alert
              variant="error"
              title="Erro de Autenticação"
              message="Email ou senha incorretos. Tente novamente."
            />
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            loading={isLoading}
            disabled={isLoading || !isValid || !watchedFields.email || !watchedFields.password}
            leftIcon={!isLoading ? faSignInAlt : undefined}
          >
            {isLoading ? 'Entrando...' : 'Entrar no Sistema'}
          </Button>
        </form>

        {/* Signup Link */}
        {isSignupEnabled() && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-600 text-center">
              Não tem uma conta?{' '}
              <a
                href="/signup"
                className="font-medium text-blue-600 hover:text-blue-700 transition-colors"
              >
                Cadastre-se aqui
              </a>
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="mt-6">
          <p className="text-xs text-gray-500 text-center">
            Desenvolvido por{' '}
            <a
              href="https://um0um.com.br"
              target="_blank"
              rel="noreferrer"
              className="font-medium text-blue-600 hover:text-blue-700 transition-colors"
            >
              101 Soluções
            </a>
          </p>
        </div>
      </div>
    </>
  );
};

export default LoginPage;
